import React, { useState, useEffect } from "react"
import { useLocation } from "react-router-dom";
import { UnderlineNav, Stack } from "@primer/react"
import { useUserContext } from "../../../context/user";
import { NavbarProps } from "../../../types/interfaces/navigation";
import { CountryResponse } from "../../../types/api/common";
import { navigationItems } from './Navbar+Data';
import { useSignOut } from "../../../hooks/useSignOut";
import { useCountriesInfiniteQuery } from "../../../hooks/useGeoQuery";
import { Button, IconButton, Token, ActionMenu, ActionList, Spinner } from "@primer/react";
import { SignOutIcon, ChevronUpIcon, CheckIcon } from '@primer/octicons-react'

export default function Navbar({ activeItem }: NavbarProps) {
    const { profile, country, setCountry } = useUserContext();

    const location = useLocation();
    const {
        data: countriesData,
        isLoading: isLoadingCountries,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage
    } = useCountriesInfiniteQuery();
    const [draftCountry, setDraftCountry] = useState<CountryResponse | null>(null);

    const isActiveRoute = (href: string) => {
        if (href === '/catalogs' && (location.pathname === '/sections' || location.pathname.startsWith('/sections'))) {
            return true;
        }
        if (href === '/sections' && (location.pathname === '/catalogs' || location.pathname.startsWith('/catalogs'))) {
            return true;
        }

        if (location.pathname === href) return true;
        if (href !== '/' && location.pathname.startsWith(href)) return true;

        return false;
    };

    const updatedItems = navigationItems(profile).map(item => ({
        ...item,
        isActive: isActiveRoute(item.href)
    }));

    const signOut = useSignOut();

    const saveCountry = () => {
        setCountry(draftCountry)
        dismissLangPopup()
        window.location.reload();
    }

    useEffect(() => {
        setDraftCountryByDefault()
    }, [country]);

    const setDraftCountryByDefault = () => {
        setDraftCountry(country)
    }

    const dismissLangPopup = () => {
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
    }

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!hasNextPage || isFetchingNextPage) return;

        const target = e.currentTarget;
        const scrollPercentage = (target.scrollTop + target.clientHeight) / target.scrollHeight;

        if (scrollPercentage > 0.8) {
            fetchNextPage();
        }
    };

    const countryIso = country?.isoCode.toLowerCase()

    const allCountries = countriesData?.pages.flatMap(page => page.list) ?? [];

    return (
        <nav className="bg-header">
            <Stack justify="space-between" direction="horizontal">
                <Stack align="center" direction="horizontal" className="mt-3">
                    <img src="/icons/logo.png" alt="logo" className="w-9 aspect-square ml-4"></img>
                    <span className="text-[20px] text-black font-medium">Aloix</span>
                </Stack>

                <Stack align="center" direction="horizontal" className="mt-3">
                    <ActionMenu onOpenChange={(open) => {
                        if (!open) {
                            setDraftCountryByDefault()
                        }
                    }}>
                        <ActionMenu.Anchor>
                            <Button trailingAction={ChevronUpIcon}>
                                {countryIso ? String(countryIso[0]).toUpperCase() + String(countryIso).slice(1) : 'Все'}
                            </Button>
                        </ActionMenu.Anchor>
                        <ActionMenu.Overlay className="!w-[232px]">
                            <div
                                className="max-h-[400px] overflow-y-auto"
                                onScroll={handleScroll}
                            >
                                <ActionList
                                    selectionVariant="multiple"
                                    role="menu"
                                    aria-label="Item"
                                    className="!w-[232px]"
                                >
                                    {isLoadingCountries && <Spinner />}
                                    <ActionList.Item
                                        key='country_all'
                                        onSelect={(event) => {
                                            event.preventDefault();
                                            setDraftCountry(null)
                                        }}
                                    >
                                        <ActionList.LeadingVisual>
                                            {draftCountry === null && <CheckIcon className="-ml-6" />}
                                        </ActionList.LeadingVisual>
                                        <div className="-ml-4">
                                            <span className="ml-2 text-gray-600">Все</span>
                                        </div>
                                    </ActionList.Item>
                                    {allCountries.map((countryItem) => (
                                        <ActionList.Item
                                            key={countryItem.id}
                                            onSelect={(event) => {
                                                event.preventDefault();
                                                setDraftCountry(countryItem)
                                            }}
                                        >
                                            <ActionList.LeadingVisual>
                                                {draftCountry?.id === countryItem.id && <CheckIcon className="-ml-6" />}
                                            </ActionList.LeadingVisual>
                                            <div className="-ml-4">
                                                <span className="ml-2 text-gray-600">{countryItem.title}</span>
                                            </div>
                                        </ActionList.Item>
                                    ))}
                                    {isFetchingNextPage && (
                                        <ActionList.Item>Загрузка...</ActionList.Item>
                                    )}
                                    {hasNextPage && !isFetchingNextPage && (
                                        <div className="p-2 text-center text-xs text-gray-500">
                                            Прокрутите для загрузки еще
                                        </div>
                                    )}
                                </ActionList>
                            </div>
                            <Stack direction="horizontal" gap='normal' className='block mt-3 ml-[130px] mb-[10px]'>
                                <Button
                                    onClick={saveCountry}
                                    size='small'
                                    variant="primary">
                                    Применить
                                </Button>
                            </Stack>
                        </ActionMenu.Overlay>
                    </ActionMenu>
                    <IconButton icon={SignOutIcon} aria-label="Выход" onClick={signOut} />
                    <Token text={profile?.username} size="xlarge" className="mr-4" />
                </Stack>
            </Stack>
            <UnderlineNav aria-label="Навигация">
                {updatedItems.map((item) => {
                    const Icon = item.icon;
                    return (
                        <UnderlineNav.Item
                            key={item.href}
                            href={item.href}
                            aria-current={item.isActive ? "page" : undefined}
                        >
                            <Stack align="center" direction="horizontal">
                                <Icon />
                                {item.text}
                            </Stack>
                        </UnderlineNav.Item>
                    );
                })}
            </UnderlineNav>
        </nav>
    );
}